import axios from 'axios';
import { apiUrl } from '../../api/endpoints';
import { getErrorMessage } from '../../utils/errorUtils';
import {
    cloneTemplateDetailsForWidgetUpload,
    normalizeIconPath,
    stripWidgetMinMaxUnit,
} from './templateDetailsHelpers';

const buildUserFileErrorMessage = (err, fallbackMessage = 'Please check the file and try again.') =>
    getErrorMessage(err, fallbackMessage);

export const deleteTemplateFilesBulk = async (files, showAlert, messages = {}) => {
    const clean = (files || [])
        .map(normalizeIconPath)
        .filter(Boolean);

    if (clean.length === 0) return;

    try {
        const res = await axios.post(apiUrl(`file/delete`), clean);

        if (showAlert) {
            const messageType = res.status === 207 ? "warning" : "success";
            showAlert(
                messageType,
                res.data.message ||
                    (messages.deletedFiles || 'Deleted {count} files.').replace('{count}', clean.length)
            );
        }
    } catch (err) {
        console.error("Bulk file deletion failed:", err);
        if (showAlert) {
            showAlert(
                "warning",
                messages.imageCleanupWarning ||
                    "Template updated, but some removed images could not be deleted."
            );
        }
    }
};

export const deleteTemplateFile = async (fileName, showAlert, messages = {}) => {
    const normalized = normalizeIconPath(fileName);

    if (!normalized || typeof normalized !== "string" || normalized.trim() === "") {
        const errorMsg = messages.invalidFilenameForDeletion || "Invalid filename provided for deletion.";
        if (showAlert) showAlert("error", errorMsg);
        throw new Error(errorMsg);
    }

    try {
        const body = [normalized];
        const res = await axios.post(apiUrl(`file/delete`), body);

        if (showAlert) {
            const messageType = res.status === 207 ? "warning" : "success";
            showAlert(messageType, res.data.message || messages.fileDeletedSuccess || "File deleted successfully.");
        }
    } catch (err) {
        console.error("File deletion failed:", err);
        if (showAlert) showAlert("error", buildUserFileErrorMessage(err));
        throw err;
    }
};

export const saveTemplateFile = async (file, showAlert, messages = {}, prefix = 'background-image') => {
    if (!(file instanceof File)) {
        const errorMsg = messages.invalidUploadFile || "Invalid file provided for upload.";
        if (showAlert) showAlert("error", errorMsg);
        throw new Error(errorMsg);
    }

    const suffix = file.name.split(".").pop();
    const newFileName = `${prefix}-${Date.now()}.${suffix}`;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", newFileName);
    formData.append("type", "icons");

    try {
        const res = await axios.post(apiUrl("file/upload"), formData);
        if (showAlert) {
            showAlert(
                "success",
                res.data.message || messages.fileUploadedSuccess || "File uploaded successfully."
            );
        }

        return newFileName;
    } catch (err) {
        console.error("File upload failed:", err);
        if (showAlert) showAlert("error", buildUserFileErrorMessage(err));
        throw err;
    }
};

export const uploadTemplateWidgetFiles = async (
    details,
    { deleteFile, saveFile, showAlert, fileSaveError } = {}
) => {
    try {
        const updatedDetails = cloneTemplateDetailsForWidgetUpload(details);

        if (updatedDetails.webdashboard) {
            for (const page of updatedDetails.webdashboard) {
                if (page.widgets) {
                    for (const widget of page.widgets) {
                        if (widget.type === "background-image") {
                            const isLightFileObject = widget.extra_props?.lightFile instanceof File;
                            const isDarkFileObject = widget.extra_props?.darkFile instanceof File;
                            const isFileObject = widget.extra_props?.file instanceof File;

                            if (isLightFileObject) {
                                if (widget.extra_props.lightImageUrl) {
                                    await deleteFile(widget.extra_props.lightImageUrl, showAlert);
                                }
                                widget.extra_props.lightImageUrl = await saveFile(widget.extra_props.lightFile, showAlert, 'background-image-light');
                                widget.extra_props.useThemeImages = true;
                            }
                            if (isDarkFileObject) {
                                if (widget.extra_props.darkImageUrl) {
                                    await deleteFile(widget.extra_props.darkImageUrl, showAlert);
                                }
                                widget.extra_props.darkImageUrl = await saveFile(widget.extra_props.darkFile, showAlert, 'background-image-dark');
                                widget.extra_props.useThemeImages = true;
                            }
                            if (isFileObject) {
                                if (widget.extra_props.imageUrl) {
                                    await deleteFile(widget.extra_props.imageUrl, showAlert);
                                }
                                const saved = await saveFile(widget.extra_props.file, showAlert);
                                widget.extra_props.imageUrl = saved;
                                delete widget.extra_props.file;
                            } else if (widget.extra_props?.file) {
                                delete widget.extra_props.file;
                            }
                            if (widget.extra_props?.useThemeImages && widget.extra_props?.imageUrl) {
                                await deleteFile(widget.extra_props.imageUrl, showAlert);
                                widget.extra_props.imageUrl = null;
                            }
                            if (widget.extra_props && "lightFile" in widget.extra_props) {
                                delete widget.extra_props.lightFile;
                            }
                            if (widget.extra_props && "darkFile" in widget.extra_props) {
                                delete widget.extra_props.darkFile;
                            }
                        } else if (widget.type === "image-button" && widget.extra_props?.images) {
                            widget.extra_props.images = await Promise.all(
                                widget.extra_props.images.map(async (img) => {
                                    let imageUrl = img.imageUrl;

                                    if (img.file instanceof File) {
                                        if (img.imageUrl) {
                                            await deleteFile(img.imageUrl, showAlert);
                                        }
                                        imageUrl = await saveFile(img.file, showAlert);
                                    } else if (img.file) {
                                        delete img.file;
                                    }

                                    return { ...img, imageUrl };
                                })
                            );
                        }
                    }
                }
            }
        }
        updatedDetails.webdashboard = stripWidgetMinMaxUnit(updatedDetails.webdashboard);

        return updatedDetails;
    } catch (uploadError) {
        console.error("An error occurred during the widget file upload process.", uploadError);
        uploadError.isUploadError = true;
        const errorMessage = buildUserFileErrorMessage(uploadError);
        if (showAlert) {
            showAlert(
                "error",
                `${fileSaveError || 'Failed to save changes due to a file error:'} ${errorMessage}`
            );
        }
        throw uploadError;
    }
};

export const cleanupOrphanedTemplateIcon = async (details, initialTemplate) => {
    const newIconWasUploaded =
        details?.icon &&
        details.icon.length > 0 &&
        details.icon !== initialTemplate?.icon;

    if (!newIconWasUploaded) {
        return;
    }

    try {
        const body = ['/icons/' + details.icon];
        await axios.post(apiUrl(`file/delete`), body);
        console.log(
            'Orphaned icon cleanup successful:',
            details.icon
        );
    } catch (err) {
        console.warn(
            'Non-critical error: Failed to clean up orphaned icon.',
            err
        );
    }
};

export const cleanupPreviousTemplateIcon = async (previousIcon, deleteFilesBulk, showAlert) => {
    if (!previousIcon || previousIcon.length <= 0) {
        return;
    }

    try {
        await deleteFilesBulk([previousIcon], showAlert);
        console.log("Successfully cleaned up previous icon:", previousIcon);
    } catch (deleteErr) {
        console.warn("Non-critical error: Failed to delete previous icon.", deleteErr);
    }
};

export const cleanupPreviousTemplateMapIcon = async ({
    previousMapIcon,
    nextMapIcon,
    pendingMapIconFile,
    mapIconMarkedForRemoval,
    deleteStoredMapIcon,
}) => {
    if (!previousMapIcon || previousMapIcon === nextMapIcon || !(pendingMapIconFile || mapIconMarkedForRemoval)) {
        return;
    }

    try {
        await deleteStoredMapIcon(previousMapIcon);
    } catch (deleteErr) {
        console.warn("Non-critical error: Failed to delete previous map icon.", deleteErr);
    }
};

export const cleanupUploadedMapIconAfterFailure = async (uploadedMapIcon, deleteStoredMapIcon) => {
    if (!uploadedMapIcon) {
        return;
    }

    try {
        await deleteStoredMapIcon(uploadedMapIcon);
    } catch (cleanupErr) {
        console.warn("Failed to clean up uploaded map icon after template update failed.", cleanupErr);
    }
};
